import { HandHeart, Home as HomeIcon, Heart } from "lucide-react";
import {
  PageHero,
  Prose,
  SectionHeading,
  FeatureBand,
  IconCardGrid,
  RelatedLinks,
  TrusteesCallout,
} from "@/components/site/PageParts";
import { Reveal } from "@/components/site/Reveal";
import { ASSETS, CONTACT } from "@/data/site";
import { Seo } from "@/components/site/Seo";

const CENTRE_CARDS = [
  {
    Icon: HandHeart,
    title: "Volunteers",
    body: "A committed team of volunteers leads horses, supports riders and keeps the yard running — the beating heart of everything we do.",
  },
  {
    Icon: HomeIcon,
    title: "Facilities",
    body: "A safe, accessible centre in Summerston with the space, equipment and calm environment our riders need to thrive.",
  },
  {
    Icon: Heart,
    title: "Horses",
    body: "Gentle, patient and carefully matched to each rider, our horses and ponies make therapeutic riding possible.",
  },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <Seo title="About — RDA Glasgow" description="Part of the national Riding for the Disabled Association, RDA Glasgow has served Greater Glasgow for over 50 years. Learn about our mission, story and centre." image={ASSETS.therapyCloseup} />
      <Seo title="About — RDA Glasgow" description="Part of the national Riding for the Disabled Association, RDA Glasgow has served Greater Glasgow for over 50 years. Learn about our mission, story and centre." image={ASSETS.therapyCloseup} />
      <PageHero
        title="About Us"
        subtitle="Part of the national Riding for the Disabled Association, proudly serving Greater Glasgow."
        image={ASSETS.therapyCloseup}
        imageAlt="A rider and horse sharing a quiet moment at RDA Glasgow"
      />

      <Prose>
        <SectionHeading eyebrow="Who we are" title="A local charity, part of a national movement" />
        <p>
          Glasgow Riding for the Disabled Association (RDA Glasgow) is part of the
          national Riding for the Disabled Association network, providing equine
          therapy to disabled people across Greater Glasgow. We are a registered
          Scottish Charity, number {CONTACT.charityNo}.
        </p>

        <div className="pt-2">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-rda-teal">
            Our mission
          </p>
          <p className="mt-3 font-heading text-2xl leading-snug text-rda-ink">
            “To give people with disabilities the opportunity to learn to ride or drive
            a horse and benefit from this therapeutic activity, and to support this
            activity by ensuring they can learn in a pleasant, safe and supportive
            environment.”
          </p>
        </div>
      </Prose>

      {/* Our story */}
      <FeatureBand
        image={ASSETS.facilityExterior}
        imageAlt="The RDA Glasgow centre and stables in Summerston"
        title="Our story"
        dark
      >
        {/* TODO: verify with RDA — 50+ years timeline and founding details */}
        <p>
          For over 50 years, RDA Glasgow has been giving people with disabilities the
          chance to experience the freedom, confidence and joy that come from being
          around horses.
        </p>
        <p>
          What began as a small group of dedicated volunteers has grown into a much-loved
          local charity — but our purpose has never changed: to put riders first, every
          single session.
        </p>
      </FeatureBand>

      {/* Our centre */}
      <section className="bg-rda-offwhite">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14 md:py-20">
          <SectionHeading
            eyebrow="Our centre"
            title="Everything a rider needs, in one welcoming place"
          />
          <div className="mt-10">
            <IconCardGrid cards={CENTRE_CARDS} />
          </div>
          <Reveal delay={0.1}>
            <div className="mt-10 overflow-hidden rounded-3xl shadow-soft">
              <img
                src={ASSETS.horsesPaddock}
                alt="Horses grazing in the paddock at the RDA Glasgow centre"
                className="h-72 md:h-96 w-full object-cover"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Trustees & team */}
      <Prose className="bg-white">
        <SectionHeading eyebrow="Trustees & team" title="Led by people who care" />
        <p>
          Our work is led by a small, dedicated group of trustees and volunteers who
          give their time, skills and energy so that our riders can keep riding. Together
          they oversee everything from horse care and coaching to fundraising and the
          day-to-day running of the centre.
        </p>
      </Prose>

      <TrusteesCallout />

      <RelatedLinks
        links={[
          { title: "Riding & Therapy", description: "See how our therapeutic riding sessions work and who they're for.", to: "/riding-therapy" },
          { title: "Volunteering", description: "Join the team that makes every session possible.", to: "/volunteering" },
          { title: "Support Us", description: "Help keep our riders in the saddle.", to: "/support-us" },
        ]}
      />
    </div>
  );
}
